/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import {
  AlertLifecycleStage,
  AlertTarget,
  AlertUrgency,
  EntityType,
  RuleMatchType,
  VisibilityType,
} from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface ActiveAlertProps {
  id: string;
  urgency: AlertUrgency;
  target: AlertTarget | null;
  targetId: string | null;
  entityType: EntityType | null;
  entityId: string | null;
  title: string | null;
  content: string;
  definitionId: string | null;
  lifecycleStage: AlertLifecycleStage;
  requiresAcknowledgement: boolean;
  dateAcknowledged: Date | null;
  acknowledgedById: string | null;
  matchType: RuleMatchType;
  parentEntityType: EntityType | null;
  parentEntityId: string | null;
  activationTime: Date | null;
  visibilityType: VisibilityType;
  escalationDurationInSeconds: number | null;
  snoozeMaxInSeconds: number | null;
  authorId: string | null;
  regionId: string | null;
  communicationCenterId: string | null;
  lookupHash: string | null;
  isGpsAlert: boolean;
  referenceEntityType: EntityType | null;
  referenceEntityId: string | null;
  isActive: boolean;
  isAudible: boolean;
  isAcknowledgementRepeatable: boolean;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class ActiveAlertRecord extends Record<ActiveAlertProps>({
  id: null as any,
  urgency: null as any,
  target: null as any,
  targetId: null as any,
  entityType: null as any,
  entityId: null as any,
  title: null as any,
  content: null as any,
  definitionId: null as any,
  lifecycleStage: null as any,
  requiresAcknowledgement: null as any,
  dateAcknowledged: null as any,
  acknowledgedById: null as any,
  matchType: null as any,
  parentEntityType: null as any,
  parentEntityId: null as any,
  activationTime: null as any,
  visibilityType: null as any,
  escalationDurationInSeconds: null as any,
  snoozeMaxInSeconds: null as any,
  authorId: null as any,
  regionId: null as any,
  communicationCenterId: null as any,
  lookupHash: null as any,
  isGpsAlert: null as any,
  referenceEntityType: null as any,
  referenceEntityId: null as any,
  isActive: null as any,
  isAudible: null as any,
  isAcknowledgementRepeatable: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    urgency: AlertUrgency;
    target: AlertTarget | null;
    targetId: string | null;
    entityType: EntityType | null;
    entityId: string | null;
    title: string | null;
    content: string;
    definitionId: string | null;
    lifecycleStage: AlertLifecycleStage;
    requiresAcknowledgement: boolean;
    dateAcknowledged: string | number | Date | null;
    acknowledgedById: string | null;
    matchType: RuleMatchType;
    parentEntityType: EntityType | null;
    parentEntityId: string | null;
    activationTime: string | number | Date | null;
    visibilityType: VisibilityType;
    escalationDurationInSeconds: number | null;
    snoozeMaxInSeconds: number | null;
    authorId: string | null;
    regionId: string | null;
    communicationCenterId: string | null;
    lookupHash: string | null;
    isGpsAlert: boolean;
    referenceEntityType: EntityType | null;
    referenceEntityId: string | null;
    isActive: boolean;
    isAudible: boolean;
    isAcknowledgementRepeatable: boolean;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): ActiveAlertRecord {
    if (json == null) {
      throw new Error('ActiveAlertRecord.fromJS: parameter cannot be null');
    }
    return new ActiveAlertRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'ActiveAlertJson.id',
      ),
      urgency: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'urgency', AlertUrgency.Information),
        'ActiveAlertJson.urgency',
      ),
      target: json.target,
      targetId: json.targetId,
      entityType: json.entityType,
      entityId: json.entityId,
      title: json.title,
      content: getFromOrThrow(json, (j) => j.content, 'ActiveAlertJson.content'),
      definitionId: json.definitionId,
      lifecycleStage: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'lifecycleStage', AlertLifecycleStage.Unspecified),
        'ActiveAlertJson.lifecycleStage',
      ),
      requiresAcknowledgement: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'requiresAcknowledgement', false),
        'ActiveAlertJson.requiresAcknowledgement',
      ),
      dateAcknowledged: json.dateAcknowledged != null ? new Date(json.dateAcknowledged) : null,
      acknowledgedById: json.acknowledgedById,
      matchType: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'matchType', RuleMatchType.Unspecified),
        'ActiveAlertJson.matchType',
      ),
      parentEntityType: json.parentEntityType,
      parentEntityId: json.parentEntityId,
      activationTime: json.activationTime != null ? new Date(json.activationTime) : null,
      visibilityType: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'visibilityType', VisibilityType.EntityOnly),
        'ActiveAlertJson.visibilityType',
      ),
      escalationDurationInSeconds: json.escalationDurationInSeconds,
      snoozeMaxInSeconds: json.snoozeMaxInSeconds,
      authorId: json.authorId,
      regionId: json.regionId,
      communicationCenterId: json.communicationCenterId,
      lookupHash: json.lookupHash,
      isGpsAlert: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isGpsAlert', false),
        'ActiveAlertJson.isGpsAlert',
      ),
      referenceEntityType: json.referenceEntityType,
      referenceEntityId: json.referenceEntityId,
      isActive: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isActive', false),
        'ActiveAlertJson.isActive',
      ),
      isAudible: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isAudible', false),
        'ActiveAlertJson.isAudible',
      ),
      isAcknowledgementRepeatable: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'isAcknowledgementRepeatable', false),
        'ActiveAlertJson.isAcknowledgementRepeatable',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'ActiveAlertJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    urgency: AlertUrgency;
    target: AlertTarget | null;
    targetId: string | null;
    entityType: EntityType | null;
    entityId: string | null;
    title: string | null;
    content: string;
    definitionId: string | null;
    lifecycleStage: AlertLifecycleStage;
    requiresAcknowledgement: boolean;
    dateAcknowledged: string | number | Date | null;
    acknowledgedById: string | null;
    matchType: RuleMatchType;
    parentEntityType: EntityType | null;
    parentEntityId: string | null;
    activationTime: string | number | Date | null;
    visibilityType: VisibilityType;
    escalationDurationInSeconds: number | null;
    snoozeMaxInSeconds: number | null;
    authorId: string | null;
    regionId: string | null;
    communicationCenterId: string | null;
    lookupHash: string | null;
    isGpsAlert: boolean;
    referenceEntityType: EntityType | null;
    referenceEntityId: string | null;
    isActive: boolean;
    isAudible: boolean;
    isAcknowledgementRepeatable: boolean;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [ActiveAlertRecord, Error[]] {
    if (json == null) {
      throw new Error('ActiveAlertRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new ActiveAlertRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'ActiveAlertJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      urgency: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'urgency', AlertUrgency.Information),
            'ActiveAlertJson.urgency',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      target: tryOrDefault(
        () => json.target,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      targetId: tryOrDefault(
        () => json.targetId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      entityType: tryOrDefault(
        () => json.entityType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      entityId: tryOrDefault(
        () => json.entityId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      title: tryOrDefault(
        () => json.title,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      content: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.content, 'ActiveAlertJson.content'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      definitionId: tryOrDefault(
        () => json.definitionId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lifecycleStage: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'lifecycleStage', AlertLifecycleStage.Unspecified),
            'ActiveAlertJson.lifecycleStage',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      requiresAcknowledgement: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'requiresAcknowledgement', false),
            'ActiveAlertJson.requiresAcknowledgement',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateAcknowledged: tryOrDefault(
        () => (json.dateAcknowledged != null ? new Date(json.dateAcknowledged) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      acknowledgedById: tryOrDefault(
        () => json.acknowledgedById,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      matchType: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'matchType', RuleMatchType.Unspecified),
            'ActiveAlertJson.matchType',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      parentEntityType: tryOrDefault(
        () => json.parentEntityType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      parentEntityId: tryOrDefault(
        () => json.parentEntityId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      activationTime: tryOrDefault(
        () => (json.activationTime != null ? new Date(json.activationTime) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      visibilityType: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'visibilityType', VisibilityType.EntityOnly),
            'ActiveAlertJson.visibilityType',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      escalationDurationInSeconds: tryOrDefault(
        () => json.escalationDurationInSeconds,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      snoozeMaxInSeconds: tryOrDefault(
        () => json.snoozeMaxInSeconds,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      authorId: tryOrDefault(
        () => json.authorId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      regionId: tryOrDefault(
        () => json.regionId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      communicationCenterId: tryOrDefault(
        () => json.communicationCenterId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      lookupHash: tryOrDefault(
        () => json.lookupHash,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isGpsAlert: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isGpsAlert', false),
            'ActiveAlertJson.isGpsAlert',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      referenceEntityType: tryOrDefault(
        () => json.referenceEntityType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      referenceEntityId: tryOrDefault(
        () => json.referenceEntityId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isActive: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isActive', false),
            'ActiveAlertJson.isActive',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isAudible: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isAudible', false),
            'ActiveAlertJson.isAudible',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      isAcknowledgementRepeatable: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'isAcknowledgementRepeatable', false),
            'ActiveAlertJson.isAcknowledgementRepeatable',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'ActiveAlertJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: ActiveAlertProps) {
    super(props);
    if (props == null) {
      throw new Error('ActiveAlertRecord constructor parameter cannot be null');
    }
  }
}

export abstract class ActiveAlertViewModelBase implements ActiveAlertProps {
  protected readonly _props: ActiveAlertProps;
  private _json?: ActiveAlertProps;

  get id(): string {
    return this._props.id;
  }
  get urgency(): AlertUrgency {
    return this._props.urgency;
  }
  get target(): AlertTarget | null {
    return this._props.target;
  }
  get targetId(): string | null {
    return this._props.targetId;
  }
  get entityType(): EntityType | null {
    return this._props.entityType;
  }
  get entityId(): string | null {
    return this._props.entityId;
  }
  get title(): string | null {
    return this._props.title;
  }
  get content(): string {
    return this._props.content;
  }
  get definitionId(): string | null {
    return this._props.definitionId;
  }
  get lifecycleStage(): AlertLifecycleStage {
    return this._props.lifecycleStage;
  }
  get requiresAcknowledgement(): boolean {
    return this._props.requiresAcknowledgement;
  }
  get dateAcknowledged(): Date | null {
    return this._props.dateAcknowledged;
  }
  get acknowledgedById(): string | null {
    return this._props.acknowledgedById;
  }
  get matchType(): RuleMatchType {
    return this._props.matchType;
  }
  get parentEntityType(): EntityType | null {
    return this._props.parentEntityType;
  }
  get parentEntityId(): string | null {
    return this._props.parentEntityId;
  }
  get activationTime(): Date | null {
    return this._props.activationTime;
  }
  get visibilityType(): VisibilityType {
    return this._props.visibilityType;
  }
  get escalationDurationInSeconds(): number | null {
    return this._props.escalationDurationInSeconds;
  }
  get snoozeMaxInSeconds(): number | null {
    return this._props.snoozeMaxInSeconds;
  }
  get authorId(): string | null {
    return this._props.authorId;
  }
  get regionId(): string | null {
    return this._props.regionId;
  }
  get communicationCenterId(): string | null {
    return this._props.communicationCenterId;
  }
  get lookupHash(): string | null {
    return this._props.lookupHash;
  }
  get isGpsAlert(): boolean {
    return this._props.isGpsAlert;
  }
  get referenceEntityType(): EntityType | null {
    return this._props.referenceEntityType;
  }
  get referenceEntityId(): string | null {
    return this._props.referenceEntityId;
  }
  get isActive(): boolean {
    return this._props.isActive;
  }
  get isAudible(): boolean {
    return this._props.isAudible;
  }
  get isAcknowledgementRepeatable(): boolean {
    return this._props.isAcknowledgementRepeatable;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isCritical() {
    return this._props.urgency === AlertUrgency.Critical;
  }
  public get isInformation() {
    return this._props.urgency === AlertUrgency.Information;
  }
  public get isWarning() {
    return this._props.urgency === AlertUrgency.Warning;
  }
  public get isTargetCard() {
    return this._props.target === AlertTarget.Card;
  }
  public get isEntityTypeCard() {
    return this._props.entityType === EntityType.Card;
  }
  public get isParentEntityTypeCard() {
    return this._props.parentEntityType === EntityType.Card;
  }
  public get isReferenceEntityTypeCard() {
    return this._props.referenceEntityType === EntityType.Card;
  }
  public get isTargetCompany() {
    return this._props.target === AlertTarget.Company;
  }
  public get isEntityTypeCompany() {
    return this._props.entityType === EntityType.Company;
  }
  public get isParentEntityTypeCompany() {
    return this._props.parentEntityType === EntityType.Company;
  }
  public get isReferenceEntityTypeCompany() {
    return this._props.referenceEntityType === EntityType.Company;
  }
  public get isFleetStatusViewer() {
    return this._props.target === AlertTarget.FleetStatusViewer;
  }
  public get isTargetRegion() {
    return this._props.target === AlertTarget.Region;
  }
  public get isEntityTypeRegion() {
    return this._props.entityType === EntityType.Region;
  }
  public get isParentEntityTypeRegion() {
    return this._props.parentEntityType === EntityType.Region;
  }
  public get isVisibilityTypeRegion() {
    return this._props.visibilityType === VisibilityType.Region;
  }
  public get isReferenceEntityTypeRegion() {
    return this._props.referenceEntityType === EntityType.Region;
  }
  public get isEntityTypeAirport() {
    return this._props.entityType === EntityType.Airport;
  }
  public get isParentEntityTypeAirport() {
    return this._props.parentEntityType === EntityType.Airport;
  }
  public get isReferenceEntityTypeAirport() {
    return this._props.referenceEntityType === EntityType.Airport;
  }
  public get isEntityTypeBase() {
    return this._props.entityType === EntityType.Base;
  }
  public get isParentEntityTypeBase() {
    return this._props.parentEntityType === EntityType.Base;
  }
  public get isReferenceEntityTypeBase() {
    return this._props.referenceEntityType === EntityType.Base;
  }
  public get isEntityTypeCommunicationCenter() {
    return this._props.entityType === EntityType.CommunicationCenter;
  }
  public get isParentEntityTypeCommunicationCenter() {
    return this._props.parentEntityType === EntityType.CommunicationCenter;
  }
  public get isReferenceEntityTypeCommunicationCenter() {
    return this._props.referenceEntityType === EntityType.CommunicationCenter;
  }
  public get isEntityTypeControlZone() {
    return this._props.entityType === EntityType.ControlZone;
  }
  public get isParentEntityTypeControlZone() {
    return this._props.parentEntityType === EntityType.ControlZone;
  }
  public get isReferenceEntityTypeControlZone() {
    return this._props.referenceEntityType === EntityType.ControlZone;
  }
  public get isEntityTypeLocation() {
    return this._props.entityType === EntityType.Location;
  }
  public get isParentEntityTypeLocation() {
    return this._props.parentEntityType === EntityType.Location;
  }
  public get isReferenceEntityTypeLocation() {
    return this._props.referenceEntityType === EntityType.Location;
  }
  public get isEntityTypeMedicalFacility() {
    return this._props.entityType === EntityType.MedicalFacility;
  }
  public get isParentEntityTypeMedicalFacility() {
    return this._props.parentEntityType === EntityType.MedicalFacility;
  }
  public get isReferenceEntityTypeMedicalFacility() {
    return this._props.referenceEntityType === EntityType.MedicalFacility;
  }
  public get isEntityTypeOutOfServiceEvent() {
    return this._props.entityType === EntityType.OutOfServiceEvent;
  }
  public get isParentEntityTypeOutOfServiceEvent() {
    return this._props.parentEntityType === EntityType.OutOfServiceEvent;
  }
  public get isReferenceEntityTypeOutOfServiceEvent() {
    return this._props.referenceEntityType === EntityType.OutOfServiceEvent;
  }
  public get isEntityTypePaipEvent() {
    return this._props.entityType === EntityType.PaipEvent;
  }
  public get isParentEntityTypePaipEvent() {
    return this._props.parentEntityType === EntityType.PaipEvent;
  }
  public get isReferenceEntityTypePaipEvent() {
    return this._props.referenceEntityType === EntityType.PaipEvent;
  }
  public get isEntityTypePerson() {
    return this._props.entityType === EntityType.Person;
  }
  public get isParentEntityTypePerson() {
    return this._props.parentEntityType === EntityType.Person;
  }
  public get isReferenceEntityTypePerson() {
    return this._props.referenceEntityType === EntityType.Person;
  }
  public get isEntityTypeProgram() {
    return this._props.entityType === EntityType.Program;
  }
  public get isParentEntityTypeProgram() {
    return this._props.parentEntityType === EntityType.Program;
  }
  public get isReferenceEntityTypeProgram() {
    return this._props.referenceEntityType === EntityType.Program;
  }
  public get isEntityTypeTimer() {
    return this._props.entityType === EntityType.Timer;
  }
  public get isParentEntityTypeTimer() {
    return this._props.parentEntityType === EntityType.Timer;
  }
  public get isReferenceEntityTypeTimer() {
    return this._props.referenceEntityType === EntityType.Timer;
  }
  public get isEntityTypeUnit() {
    return this._props.entityType === EntityType.Unit;
  }
  public get isParentEntityTypeUnit() {
    return this._props.parentEntityType === EntityType.Unit;
  }
  public get isReferenceEntityTypeUnit() {
    return this._props.referenceEntityType === EntityType.Unit;
  }
  public get isEntityTypeUnspecified() {
    return this._props.entityType === EntityType.Unspecified;
  }
  public get isLifecycleStageUnspecified() {
    return this._props.lifecycleStage === AlertLifecycleStage.Unspecified;
  }
  public get isMatchTypeUnspecified() {
    return this._props.matchType === RuleMatchType.Unspecified;
  }
  public get isParentEntityTypeUnspecified() {
    return this._props.parentEntityType === EntityType.Unspecified;
  }
  public get isReferenceEntityTypeUnspecified() {
    return this._props.referenceEntityType === EntityType.Unspecified;
  }
  public get isEntityTypeVehicle() {
    return this._props.entityType === EntityType.Vehicle;
  }
  public get isParentEntityTypeVehicle() {
    return this._props.parentEntityType === EntityType.Vehicle;
  }
  public get isReferenceEntityTypeVehicle() {
    return this._props.referenceEntityType === EntityType.Vehicle;
  }
  public get isPreAssignment() {
    return this._props.lifecycleStage === AlertLifecycleStage.PreAssignment;
  }
  public get isDropoffLocation() {
    return this._props.matchType === RuleMatchType.DropoffLocation;
  }
  public get isPickupLocation() {
    return this._props.matchType === RuleMatchType.PickupLocation;
  }
  public get isAuthor() {
    return this._props.visibilityType === VisibilityType.Author;
  }
  public get isCommunicationsCenter() {
    return this._props.visibilityType === VisibilityType.CommunicationsCenter;
  }
  public get isEntityOnly() {
    return this._props.visibilityType === VisibilityType.EntityOnly;
  }
  public get isTargetOnly() {
    return this._props.visibilityType === VisibilityType.TargetOnly;
  }

  public isAnyUrgency(...alertUrgency: AlertUrgency[]) {
    return alertUrgency.includes(this._props.urgency);
  }
  public isAnyTarget(...alertTarget: AlertTarget[]) {
    return this._props.target != null && alertTarget.includes(this._props.target);
  }
  public isAnyEntityType(...entityType: EntityType[]) {
    return this._props.entityType != null && entityType.includes(this._props.entityType);
  }
  public isAnyLifecycleStage(...alertLifecycleStage: AlertLifecycleStage[]) {
    return alertLifecycleStage.includes(this._props.lifecycleStage);
  }
  public isAnyMatchType(...ruleMatchType: RuleMatchType[]) {
    return ruleMatchType.includes(this._props.matchType);
  }
  public isAnyParentEntityType(...entityType: EntityType[]) {
    return (
      this._props.parentEntityType != null && entityType.includes(this._props.parentEntityType)
    );
  }
  public isAnyVisibilityType(...visibilityType: VisibilityType[]) {
    return visibilityType.includes(this._props.visibilityType);
  }
  public isAnyReferenceEntityType(...entityType: EntityType[]) {
    return (
      this._props.referenceEntityType != null &&
      entityType.includes(this._props.referenceEntityType)
    );
  }

  constructor(props: ActiveAlertProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        urgency: this._props.urgency,
        target: this._props.target,
        targetId: this._props.targetId,
        entityType: this._props.entityType,
        entityId: this._props.entityId,
        title: this._props.title,
        content: this._props.content,
        definitionId: this._props.definitionId,
        lifecycleStage: this._props.lifecycleStage,
        requiresAcknowledgement: this._props.requiresAcknowledgement,
        dateAcknowledged: this._props.dateAcknowledged,
        acknowledgedById: this._props.acknowledgedById,
        matchType: this._props.matchType,
        parentEntityType: this._props.parentEntityType,
        parentEntityId: this._props.parentEntityId,
        activationTime: this._props.activationTime,
        visibilityType: this._props.visibilityType,
        escalationDurationInSeconds: this._props.escalationDurationInSeconds,
        snoozeMaxInSeconds: this._props.snoozeMaxInSeconds,
        authorId: this._props.authorId,
        regionId: this._props.regionId,
        communicationCenterId: this._props.communicationCenterId,
        lookupHash: this._props.lookupHash,
        isGpsAlert: this._props.isGpsAlert,
        referenceEntityType: this._props.referenceEntityType,
        referenceEntityId: this._props.referenceEntityId,
        isActive: this._props.isActive,
        isAudible: this._props.isAudible,
        isAcknowledgementRepeatable: this._props.isAcknowledgementRepeatable,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllActiveAlertEntities = (state: AppState) =>
  state.entityStores.activeAlert.entities;

export const selectActiveAlertById = (id: string) => (state: AppState) =>
  state.entityStores.activeAlert.entities.get(id);

export const selectActiveAlertMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.activeAlert.meta.get(id);

export const selectActiveAlertAndMetadataById = (id: string) =>
  createSelector(
    selectActiveAlertById(id),
    selectActiveAlertMetadataById(id),
    (activeAlert, meta) => ({ activeAlert, meta }),
  );

export function selectActiveAlertEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllActiveAlertEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, ActiveAlertRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectActiveAlertEntitiesByUrgency(urgency: AlertUrgency) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities.filter((e) => e.urgency === urgency).valueSeq(),
  );
}

export function selectActiveAlertEntitiesByTarget(target: AlertTarget) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities.filter((e) => e.target === target).valueSeq(),
  );
}

export function selectActiveAlertEntitiesByTargetId(targetId: string) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities.filter((e) => e.targetId === targetId).valueSeq(),
  );
}

export function selectActiveAlertEntitiesByEntityType(entityType: EntityType) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities.filter((e) => e.entityType === entityType).valueSeq(),
  );
}

export function selectActiveAlertEntitiesByEntityId(entityId: string) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities.filter((e) => e.entityId === entityId).valueSeq(),
  );
}

export function selectActiveAlertEntitiesByTitle(title: string) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities.filter((e) => e.title === title).valueSeq(),
  );
}

export function selectActiveAlertEntitiesByContent(content: string) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities.filter((e) => e.content === content).valueSeq(),
  );
}

export function selectActiveAlertEntitiesByDefinitionId(definitionId: string) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities.filter((e) => e.definitionId === definitionId).valueSeq(),
  );
}

export function selectActiveAlertEntitiesByLifecycleStage(lifecycleStage: AlertLifecycleStage) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities.filter((e) => e.lifecycleStage === lifecycleStage).valueSeq(),
  );
}

export function selectActiveAlertEntitiesByRequiresAcknowledgement(
  requiresAcknowledgement: boolean,
) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities.filter((e) => e.requiresAcknowledgement === requiresAcknowledgement).valueSeq(),
  );
}

export function selectActiveAlertEntitiesByDateAcknowledged(dateAcknowledged: Date) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities.filter((e) => e.dateAcknowledged === dateAcknowledged).valueSeq(),
  );
}

export function selectActiveAlertEntitiesByAcknowledgedById(acknowledgedById: string) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities.filter((e) => e.acknowledgedById === acknowledgedById).valueSeq(),
  );
}

export function selectActiveAlertEntitiesByMatchType(matchType: RuleMatchType) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities.filter((e) => e.matchType === matchType).valueSeq(),
  );
}

export function selectActiveAlertEntitiesByParentEntityType(parentEntityType: EntityType) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities.filter((e) => e.parentEntityType === parentEntityType).valueSeq(),
  );
}

export function selectActiveAlertEntitiesByParentEntityId(parentEntityId: string) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities.filter((e) => e.parentEntityId === parentEntityId).valueSeq(),
  );
}

export function selectActiveAlertEntitiesByActivationTime(activationTime: Date) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities.filter((e) => e.activationTime === activationTime).valueSeq(),
  );
}

export function selectActiveAlertEntitiesByVisibilityType(visibilityType: VisibilityType) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities.filter((e) => e.visibilityType === visibilityType).valueSeq(),
  );
}

export function selectActiveAlertEntitiesByEscalationDurationInSeconds(
  escalationDurationInSeconds: number,
) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities
      .filter((e) => e.escalationDurationInSeconds === escalationDurationInSeconds)
      .valueSeq(),
  );
}

export function selectActiveAlertEntitiesBySnoozeMaxInSeconds(snoozeMaxInSeconds: number) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities.filter((e) => e.snoozeMaxInSeconds === snoozeMaxInSeconds).valueSeq(),
  );
}

export function selectActiveAlertEntitiesByAuthorId(authorId: string) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities.filter((e) => e.authorId === authorId).valueSeq(),
  );
}

export function selectActiveAlertEntitiesByRegionId(regionId: string) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities.filter((e) => e.regionId === regionId).valueSeq(),
  );
}

export function selectActiveAlertEntitiesByCommunicationCenterId(communicationCenterId: string) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities.filter((e) => e.communicationCenterId === communicationCenterId).valueSeq(),
  );
}

export function selectActiveAlertEntitiesByLookupHash(lookupHash: string) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities.filter((e) => e.lookupHash === lookupHash).valueSeq(),
  );
}

export function selectActiveAlertEntitiesByIsGpsAlert(isGpsAlert: boolean) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities.filter((e) => e.isGpsAlert === isGpsAlert).valueSeq(),
  );
}

export function selectActiveAlertEntitiesByReferenceEntityType(referenceEntityType: EntityType) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities.filter((e) => e.referenceEntityType === referenceEntityType).valueSeq(),
  );
}

export function selectActiveAlertEntitiesByReferenceEntityId(referenceEntityId: string) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities.filter((e) => e.referenceEntityId === referenceEntityId).valueSeq(),
  );
}

export function selectActiveAlertEntitiesByIsActive(isActive: boolean) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities.filter((e) => e.isActive === isActive).valueSeq(),
  );
}

export function selectActiveAlertEntitiesByIsAudible(isAudible: boolean) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities.filter((e) => e.isAudible === isAudible).valueSeq(),
  );
}

export function selectActiveAlertEntitiesByIsAcknowledgementRepeatable(
  isAcknowledgementRepeatable: boolean,
) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities
      .filter((e) => e.isAcknowledgementRepeatable === isAcknowledgementRepeatable)
      .valueSeq(),
  );
}

export function selectActiveAlertEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllActiveAlertEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
